import { useEffect } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Coffee, MessageSquare, Calendar, CalendarPlus, CalendarClock, AlarmClock, Users, UserPlus, Orbit, Crown, Bell, Megaphone, CheckCheck, AtSign, Briefcase, Flag, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

function pluralWord(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({
    meta: [
      { title: "Уведомления — TurkeyDrive" },
      { name: "description", content: "Лента событий: приглашения на кофе, ответы на запросы и новости о ваших мероприятиях." },
      { property: "og:title", content: "Уведомления — TurkeyDrive" },
      { property: "og:description", content: "Лента событий: приглашения на кофе, ответы на запросы и новости о ваших мероприятиях." },
    ],
  }),
  component: NotificationsPage,
});

const KIND_ICON: Record<string, typeof Bell> = {
  coffee_invite: Coffee,
  coffee_accepted: Coffee,
  coffee_declined: Coffee,
  coffee_followup: Coffee,
  request_response: MessageSquare,
  request_published: MessageSquare,
  request_reply: MessageSquare,
  request_mention: AtSign,
  request_unanswered: MessageSquare,
  request_response_reported: Flag,
  neighbor_request: MessageSquare,
  event_published: Calendar,
  event_new: CalendarPlus,
  event_reminder: CalendarClock,
  event_soon: AlarmClock,
  event_follow_up: Users,
  event_rsvp: UserPlus,
  circle_published: Orbit,
  circle_cohost: Users,
  role_granted: Crown,
  announcement: Megaphone,
  work_response: Briefcase,
  concierge_request: ShieldCheck,
  work_reported: Flag,
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMin = Math.floor((Date.now() - then) / 60_000);
  if (diffMin < 1) return "только что";
  if (diffMin < 60) return `${diffMin} ${pluralWord(diffMin, "минуту", "минуты", "минут")} назад`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ${pluralWord(diffH, "час", "часа", "часов")} назад`;
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

type NotificationRow = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  url: string | null;
  created_at: string;
  read_at: string | null;
};

function NotificationsPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  // Назад по истории; если историю открыли напрямую — на главную
  const goBack = () => {
    if (router.history.canGoBack()) router.history.back();
    else router.navigate({ to: "/home" });
  };


  const { data: items, isLoading } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: async (): Promise<NotificationRow[]> => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
  };

  const markOne = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("mark_notifications_read", { p_ids: [id] });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const markAll = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("mark_notifications_read");
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast("Все уведомления отмечены как прочитанные");
    },
  });

  const hasUnread = (items ?? []).some((n) => !n.read_at);

  // Через 1,5 сек на странице отмечаем прочитанными только показанные непрочитанные
  const shownUnreadKey = (items ?? []).filter((n) => !n.read_at).map((n) => n.id).join(",");
  useEffect(() => {
    if (!shownUnreadKey) return;
    const ids = shownUnreadKey.split(",");
    const t = window.setTimeout(async () => {
      const { error } = await supabase.rpc("mark_notifications_read", { p_ids: ids });
      if (!error) queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [shownUnreadKey, queryClient]);

  const openItem = (n: NotificationRow) => {
    if (!n.read_at) markOne.mutate(n.id);
    if (n.url) {
      const u = new URL(n.url, window.location.origin);
      navigate({ to: u.pathname as any, search: Object.fromEntries(u.searchParams) as any });
    }
  };

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-6 md:px-6">
      <button
        type="button"
        onClick={goBack}
        className="mb-4 text-sm text-td-muted hover:text-td-dark"
      >
        ← Назад
      </button>
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-td-dark">Уведомления</h1>
        {hasUnread && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="flex min-h-[36px] items-center gap-1.5 rounded-full border border-td-teal px-4 py-1.5 text-[13px] font-semibold text-td-teal transition hover:bg-primary-50 disabled:opacity-50"
            >
              <CheckCheck className="h-4 w-4" aria-hidden="true" />
              Отметить все как прочитанные
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-td-muted">Загружаем…</p>
      ) : !items || items.length === 0 ? (
        <div className="rounded-2xl border border-td-line bg-white px-6 py-12 text-center">
          <Bell className="mx-auto mb-3 h-8 w-8 text-td-muted" aria-hidden="true" />
          <p className="text-sm text-td-muted">
            Пока пусто. Здесь будут появляться приглашения на кофе, ответы на твои запросы и новости о твоих мероприятиях
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((n) => {
            const Icon = KIND_ICON[n.kind] ?? Bell;
            const unread = !n.read_at;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className={
                    "flex w-full items-start gap-3 rounded-2xl border border-td-line px-4 py-3 text-left transition hover:border-td-teal " +
                    (unread ? "bg-primary-50" : "bg-white")
                  }
                >
                  <span className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-td-soft">
                    <Icon className="h-4.5 w-4.5 text-td-teal" aria-hidden="true" />
                    {unread && (
                      <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-td-orange" aria-label="Непрочитано" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold text-td-dark">{n.title}</span>
                    {n.body && (
                      <span className="mt-0.5 block text-[13px] leading-snug text-td-muted">{n.body}</span>
                    )}
                  </span>
                  <span className="shrink-0 pt-1 text-[11px] text-td-muted">{relativeTime(n.created_at)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
